@if (('true' | isLoading)()) {
  <div class="mt-4 flex flex-col gap-10">
    <div class="flex justify-between">
      <p-skeleton height="2.5rem" width="14rem" />
      <p-skeleton height="2.5rem" width="14rem" />
    </div>
    <div class="flex gap-4">
      <p-skeleton height="8rem" class="grow" />
      <p-skeleton height="8rem" class="grow" />
      <p-skeleton height="8rem" class="grow" />
      <p-skeleton height="8rem" class="grow" />
    </div>
    <summary-chart-skeleton />
    <timeline-skeleton />
    <div class="flex">
      <summary-chart-skeleton class="grow" />
      <summary-chart-skeleton class="grow" />
      <summary-chart-skeleton class="grow" />
    </div>
  </div>
} @else {
  <warning-overview />
  <div class="flex flex-col gap-8">
    @if (cutOffDateState.currentPeriod()) {
      <div class="flex items-center justify-between">
        <currency-and-date />
        <div class="flex items-end gap-4">
          <p-button
            variant="outlined"
            (onClick)="toggleExportGraphs()"
            [injectAttribute]="[
              {
                CSSPath: 'button',
                attribute: 'analytics-label',
                value:
                  { element: 'button', action: 'export_mode_activated' }
                  | analyticsLabelValue,
              },
            ]">
            <ng-icon
              [name]="exportGraphs() ? 'phosphorX' : 'phosphorDownloadSimple'"
              class="text-base text-muted" />
            <span>{{ exportGraphs() ? 'Cancel' : 'Export' }}</span>
          </p-button>
          <cut-off-date />
        </div>
      </div>
    }

    @if (exportGraphs()) {
      <selection-banner
        [selectedCount]="selectedWidgets().length"
        (selectAll)="selectAllWidgets()"
        (clear)="clearWidgets()"
        (continueClick)="openExportModal()" />
    }

    @if (overviewResumeState.failed()) {
      <!-- LEN-2078: show an error instead of the previous lender's figures.
           The cut-off and currency controls above stay visible so the user
           can retry by changing the selection. -->
      <empty-state-page
        subTitle="We couldn't load this overview. Try a different cut-off date or currency." />
    } @else {
      <core-metrics
        [exportMode]="exportGraphs()"
        [selectedWidgets]="selectedWidgets()"
        [currency]="displayCurrency()"
        (widgetToggle)="
          onWidgetToggle($event.checked, $event.el)
        "></core-metrics>

      @if (firstChart()) {
        <selectable-widget
          #swFirstChart
          [exportMode]="exportGraphs()"
          [selected]="isWidgetSelected(swFirstChart.nativeEl)"
          (widgetToggle)="onWidgetToggle($event, swFirstChart.nativeEl)">
          <internal-chart
            [data]="firstChart()"
            [currency]="displayCurrency()"
            tooltip="The consolidated value is calculated by averaging the borrowing base and outstanding debt of the active facilities as of the cutoff date."
            height="300px"></internal-chart>
        </selectable-widget>
      }

      <graphs-carousel
        [exportMode]="exportGraphs()"
        [selectedWidgets]="selectedWidgets()"
        (widgetToggle)="
          onWidgetToggle($event.checked, $event.el)
        "></graphs-carousel>

      @if (overviewTimeline()) {
        <selectable-widget
          #swTimeline
          [exportMode]="exportGraphs()"
          [selected]="isWidgetSelected(swTimeline.nativeEl)"
          (widgetToggle)="onWidgetToggle($event, swTimeline.nativeEl)">
          <div class="card-border relative col-span-full flex flex-col p-6">
            <div class="absolute flex items-center gap-2">
              <span
                class="card-grey flex h-10 w-10 items-center justify-center">
                <ng-icon name="phosphorCalendarDots" class="text-2xl"></ng-icon>
              </span>
              <h4 class="font-bold">DURATION AND AMORTIZATION</h4>
            </div>
            <timeline [dates]="overviewTimeline()!"></timeline>
          </div>
        </selectable-widget>
      }

      @if (matrixDataTable().length > 0) {
        <selectable-widget
          #swMatrix
          [exportMode]="exportGraphs()"
          [selected]="isWidgetSelected(swMatrix.nativeEl)"
          (widgetToggle)="onWidgetToggle($event, swMatrix.nativeEl)">
          <div class="card-border col-span-2 p-6">
            <div class="mb-4 flex items-center gap-4">
              <ng-icon name="phosphorTable" size="30px" />
              <h2>PORTFOLIO CHARACTERISTICS</h2>
            </div>
            <p-table
              [value]="matrixDataTable()"
              styleClass="p-datatable-gridlines">
              @let headerKey = 'asset_type';
              <ng-template pTemplate="header">
                <tr>
                  <th>Metric</th>
                  @for (item of matrixDataTable(); track $index) {
                    @if (item.key === headerKey) {
                      @for (value of item.values; track $index) {
                        <th>{{ value }}</th>
                      }
                    }
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                @let hiddenKeys = [headerKey, 'lender'];
                @let shouldShow = !hiddenKeys.includes(row.key);
                @if (shouldShow) {
                  <tr>
                    <td>
                      <strong>{{
                        row.key | fromSnakeCase | nameChange
                      }}</strong>
                    </td>
                    @for (value of row.values; track $index) {
                      <td>
                        @if (row.data_type === 'CURRENCY') {
                          <!-- MR !1265 review, B1: the API sends strings;
                               "" (None) is a dash, never $0 (LEN-2460). -->
                          @let amount = value | matrixAmount;
                          @if (amount !== null) {
                            <currency-amount
                              [value]="amount"
                              [currency]="displayCurrency()" />
                          } @else {
                            <span>—</span>
                          }
                        } @else if (row.data_type === 'PERCENTAGE') {
                          {{ value | percentFormat }}%
                        } @else {
                          <span>{{ value }}</span>
                        }
                      </td>
                    }
                  </tr>
                }
              </ng-template>
            </p-table>
          </div>
        </selectable-widget>
      }

      <facilities
        [exportMode]="exportGraphs()"
        [selectedWidgets]="selectedWidgets()"
        (widgetToggle)="onWidgetToggle($event.checked, $event.el)"></facilities>
    }
  </div>
}

@if (exportModalVisible()) {
  <export-report-modal
    [(visible)]="exportModalVisible"
    [selectedCount]="selectedWidgets().length"
    [previewItems]="previewItems()"
    [loading]="isExporting()"
    [fileNameData]="defaultExportFileName()"
    (back)="exportModalVisible.set(false)"
    (download)="onExportDownload($event)" />
}
